<script setup lang="ts">
import type { StyleValue } from "vue"

import { PropertyId } from "~/classes/entity"

const props = defineProps({
  fileId: null,
  previewWidth: null
})
const { isSelectedIn } = useSelection()
const { fileIdAttr } = useGlobals()
const { state: viewModeState } = useViewMode()
const { getReactiveFile } = useCollectionView()

const reactiveFile = getReactiveFile(props.fileId, "collection-view")!

if (!reactiveFile)
  throw new Error("Must not instantiate CollectionFeedFile without a reactive file")

const file = reactiveFile.ref()

function aspectRatioStyle(file: StaticFile): StyleValue {
  const aspect = file.aspectRatio()
  if (!aspect)
    return {}
  return { "aspect-ratio": `${aspect} / 1`, "width": "100%" }
}

function previewHeight(file: StaticFile) {
  let [height, width] = [1, 1]
  if (file.mediaWidthPx() && file.mediaHeightPx())
    [height, width] = [file.mediaHeightPx()!, file.mediaWidthPx()!]
  if (file.mediaWidthCm() && file.mediaHeightCm())
    [height, width] = [file.mediaHeightCm()!, file.mediaWidthCm()!]
  return props.previewWidth * height / width
}

function hideBeforeHover(file: StaticFile, propertyId: PropertyId) {
  if (isSelectedIn(file.id, "collection-view"))
    return false
  if (propertyId === PropertyId.FILE_VIRTUAL_PREVIEW)
    return true
  if (!isDefined(file.mimeType()))
    return true
  if (propertyId === PropertyId.FILE_NAME && (file.isFolder() || file.isLink()))
    return false
  return true
}

const properties = computed(
  () => viewModeState
    .value
    .current
    .show
    .filter(p => p.propertyId !== PropertyId.FILE_VIRTUAL_PREVIEW)
    .filter(p => file.value.hasProperty(p.propertyId)),
)
</script>

<template>
  <div
    v-if="file"
    :[dataAttr(fileIdAttr)]="file.id"
    class="group feed-file"
  >
    <div
      class="feed-file-inner"
      :class="{ selected: isSelectedIn(file.id, 'collection-view') }"
    />

    <file-thumbnail
      :file
      :selected="isSelectedIn(file.id, 'collection-view')"
      :container-width="previewWidth"
      :container-height="previewHeight(file)"
      :style="aspectRatioStyle(file)"
      flat
    />
    <div
      class="feed-file-properties"
    >
      <file-property-view
        v-for="{ propertyId, size, color } in properties"
        :key="propertyId"
        :property-id
        :size
        :reactive-file
        :color
        bg
        text-alignment="left"
        class="feed-file-property"
        :class="{ hide: hideBeforeHover(file, propertyId) }"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.feed-file
  @apply relative w-full cursor-pointer
.feed-file-inner
  @apply pointer-events-none absolute z-gesture-layer border-$global-blue-primary rounded-[2px] -bottom-0.5 -left-0.5 -right-0.5 -top-0.5 group-hover:border-2
  &.selected
    @apply border-2
.feed-file-properties
  @apply absolute bottom-0 left-0 max-w-full gap-0.5 p-1 overflow-hidden items-center flex
.feed-file-property
  &.hide
    @apply hidden group-hover:flex
  &:not(.hide)
    @apply flex
</style>
